import type { LocalDateTimeString } from '../types/PublicBookingChannelTypes'

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/**
 * Formats an iCalendar timestamp.
 *
 * Slot times arrive without an offset and are local to the garage, so they
 * are emitted as floating local times — the same convention the calendar
 * spec uses for "7am wherever you are". Converting to UTC here would shift
 * the appointment for anyone whose device sits in another timezone.
 */
function toIcsLocal(value: LocalDateTimeString): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})/.exec(value)
  if (match) {
    const [, year, month, day, hour, minute] = match
    return `${year}${month}${day}T${hour}${minute}00`
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null

  return (
    `${parsed.getFullYear()}${pad(parsed.getMonth() + 1)}${pad(parsed.getDate())}` +
    `T${pad(parsed.getHours())}${pad(parsed.getMinutes())}00`
  )
}

/** iCalendar requires CRLF line endings and escaped separators. */
function escapeText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
}

export interface BookingCalendarEvent {
  reference: string
  businessName: string
  summary: string
  location?: string | null
  start: LocalDateTimeString
  end: LocalDateTimeString
}

/** The calendar entry's title: the garage, then the services booked. */
export function bookingCalendarSummary(businessName: string, serviceNames: string[]): string {
  return `${businessName}: ${serviceNames.join(', ')}`
}

/** Builds an .ics document for the confirmed booking. */
export function buildBookingIcs(event: BookingCalendarEvent): string | null {
  const start = toIcsLocal(event.start)
  const end = toIcsLocal(event.end)
  if (!start || !end) return null

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//AutoChain//Booking Widget//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${escapeText(event.reference)}@autochain.co.uk`,
    `SUMMARY:${escapeText(event.summary)}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `DESCRIPTION:${escapeText(`Booking reference ${event.reference} with ${event.businessName}`)}`,
  ]

  if (event.location) {
    lines.push(`LOCATION:${escapeText(event.location)}`)
  }

  lines.push('END:VEVENT', 'END:VCALENDAR')

  return lines.join('\r\n')
}

/** Data URL suitable for a download link; null when the event is unusable. */
export function buildIcsDataUrl(event: BookingCalendarEvent): string | null {
  const ics = buildBookingIcs(event)
  if (!ics) return null
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`
}
